import axiosInstance from '../services/axiosConfig';

// RMMS API Base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://app-rmmsapi.azurewebsites.net/api";

// Use axiosInstance for all API calls (includes JWT token automatically)
const axios = axiosInstance;

// Helper function to get stored user data
const getUserData = () => {
  const companyId = sessionStorage.getItem('companyId') || sessionStorage.getItem('user_companyId') || 1;
  const userId = sessionStorage.getItem('userId') || sessionStorage.getItem('user_id') || 1;

  return { userId: parseInt(userId), companyId: parseInt(companyId) };
};

const api = {
  // Work Orders API
  workOrders: {
    list: async (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.workOrderType) params.append('workOrderType', filters.workOrderType);
      if (filters.status) params.append('status', filters.status);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.assignedUserId) params.append('assignedUserId', filters.assignedUserId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.companyId) params.append('companyId', filters.companyId);
      const res = await axios.get(`${API_BASE_URL}/WorkOrder/list?${params.toString()}`);
      return res.data;
    },
    template: {
      create: async (payload) => {
        const res = await axios.post(`${API_BASE_URL}/WorkOrder/template/create`, payload);
        return res.data;
      },
      list: async () => {
        return await api.workOrders.list({ workOrderType: 'Template' });
      }
    },
    periodic: {
      create: async (payload) => {
        const res = await axios.post(`${API_BASE_URL}/WorkOrder/periodic/create`, payload);
        return res.data;
      },
      listActive: async () => {
        const res = await axios.get(`${API_BASE_URL}/WorkOrder/periodic/active`);
        return res.data;
      },
      generateNow: async () => {
        const res = await axios.post(`${API_BASE_URL}/WorkOrder/periodic/generate`);
        return res.data;
      },
      update: async (id, payload) => {
        const res = await axios.put(`${API_BASE_URL}/WorkOrder/periodic/${id}`, payload);
        return res.data;
      },
      activate: async (id) => {
        const res = await axios.post(`${API_BASE_URL}/WorkOrder/periodic/${id}/activate`);
        return res.data;
      },
      deactivate: async (id) => {
        const res = await axios.post(`${API_BASE_URL}/WorkOrder/periodic/${id}/deactivate`);
        return res.data;
      },
      delete: async (id) => {
        const res = await axios.delete(`${API_BASE_URL}/WorkOrder/periodic/${id}`);
        return res.data;
      }
    },
    takeWorkOrder: async (workOrderId, username, base64Photo = null) => {
      const res = await axios.post(`${API_BASE_URL}/WorkOrder/${workOrderId}/take`, {
        Username: username,
        Base64Photo: base64Photo
      });
      return res.data;
    },
    closeWorkOrder: async (workOrderId, data = {}) => {
      const res = await axios.post(`${API_BASE_URL}/WorkOrder/${workOrderId}/close`, data);
      return res.data;
    },
    get: async (workOrderId) => {
      const res = await axios.get(`${API_BASE_URL}/WorkOrder/${workOrderId}`);
      return res.data;
    },
    getHistory: async (workOrderId) => {
      const res = await axios.get(`${API_BASE_URL}/WorkOrder/${workOrderId}/history`);
      return res.data;
    },
    createUserRequest: async (payload) => {
      const res = await axios.post(`${API_BASE_URL}/WorkOrder/createUserRequest`, {
        Title: payload.title,
        Description: payload.description,
        Priority: payload.priority,
        AssignedUserId: payload.assignedUserId,
        DueDate: payload.dueDate,
        Location: payload.location,
        Equipment: payload.equipment,
        EstimatedHours: payload.estimatedHours,
        CreatedByUserId: payload.createdByUserId || getUserData().userId,
        CompanyId: payload.companyId || getUserData().companyId,
        Base64Attachment: payload.base64Attachment
      });
      return res.data;
    },
    update: async (workOrderId, payload) => {
      const res = await axios.put(`${API_BASE_URL}/WorkOrder/${workOrderId}`, {
        Title: payload.title,
        Description: payload.description,
        Status: payload.status || "InProgress",
        Priority: payload.priority,
        AssignedUserId: payload.assignedUserId,
        DueDate: payload.dueDate || null,
        Location: payload.location,
        Equipment: payload.equipment,
        EstimatedHours: payload.estimatedHours,
        UserId: payload.userId || getUserData().userId,
        Base64Attachment: payload.base64Attachment
      });
      return res.data;
    },
    complete: async (workOrderId, payload) => {
      const res = await axios.post(`${API_BASE_URL}/WorkOrder/${workOrderId}/complete`, {
        CompletionNotes: payload.completionNotes,
        ActualHours: payload.actualHours,
        FinalStatus: payload.finalStatus,
        UserId: payload.userId || getUserData().userId
      });
      return res.data;
    }
  },

  // Alarms API
  alarms: {
    createWorkOrder: async ({ alarmId, title, description, assignedUserId, dueDate, priority, createdByUserId, companyId, location, equipment }) => {
      const userData = getUserData();
      const res = await axios.post(`${API_BASE_URL}/WorkOrder/createWorkOrder`, {
        AlarmId: alarmId,
        Title: title,
        Description: description,
        AssignedUserId: assignedUserId,
        DueDate: dueDate,
        Priority: priority,
        CreatedByUserId: createdByUserId || userData.userId,
        CompanyId: companyId || userData.companyId,
        Location: location,
        Equipment: equipment
      });
      return res.data;
    }
  },

  // Skills API
  skills: {
    getAll: async (isActive = null) => {
      const params = isActive !== null ? `?isActive=${isActive}` : '';
      const response = await axios.get(`${API_BASE_URL}/Skills${params}`);
      return response.data;
    },
    getById: async (id) => {
      const response = await axios.get(`${API_BASE_URL}/Skills/${id}`);
      return response.data;
    },
    create: async (skill) => {
      const response = await axios.post(`${API_BASE_URL}/Skills`, skill);
      return response.data;
    },
    update: async (id, skill) => {
      const response = await axios.put(`${API_BASE_URL}/Skills/${id}`, skill);
      return response.data;
    },
    delete: async (id) => {
      const response = await axios.delete(`${API_BASE_URL}/Skills/${id}`);
      return response.data;
    }
  },

  // Employees API
  employees: {
    getAll: async (isActive = null) => {
      const params = isActive !== null ? `?isActive=${isActive}` : '';
      const response = await axios.get(`${API_BASE_URL}/Employees${params}`);
      return response.data;
    },
    getById: async (id) => {
      const response = await axios.get(`${API_BASE_URL}/Employees/${id}`);
      return response.data;
    },
    getSkills: async (id) => {
      const response = await axios.get(`${API_BASE_URL}/Employees/${id}/skills`);
      return response.data;
    },
    getBySkill: async (skillId) => {
      const response = await axios.get(`${API_BASE_URL}/Employees/by-skill/${skillId}`);
      return response.data;
    },
    create: async (employee) => {
      const response = await axios.post(`${API_BASE_URL}/Employees`, employee);
      return response.data;
    },
    update: async (id, employee) => {
      const response = await axios.put(`${API_BASE_URL}/Employees/${id}`, employee);
      return response.data;
    },
    delete: async (id) => {
      const response = await axios.delete(`${API_BASE_URL}/Employees/${id}`);
      return response.data;
    },
    addSkill: async (employeeId, skill) => {
      const response = await axios.post(`${API_BASE_URL}/Employees/${employeeId}/skills`, skill);
      return response.data;
    },
    removeSkill: async (employeeId, skillId) => {
      const response = await axios.delete(`${API_BASE_URL}/Employees/${employeeId}/skills/${skillId}`);
      return response.data;
    }
  },

  // Locations API
  locations: {
    getAll: async () => {
      const response = await axios.get(`${API_BASE_URL}/Location/list`);
      return response.data;
    },
    get: async (id) => {
      const response = await axios.get(`${API_BASE_URL}/Location/${id}`);
      return response.data;
    },
    create: async (locationData) => {
      const response = await axios.post(`${API_BASE_URL}/Location`, locationData);
      return response.data;
    },
    update: async (id, locationData) => {
      const response = await axios.put(`${API_BASE_URL}/Location/${id}`, locationData);
      return response.data;
    },
    delete: async (id) => {
      const response = await axios.delete(`${API_BASE_URL}/Location/${id}`);
      return response.data;
    }
  },

  // Equipment API
  equipment: {
    getAll: async () => {
      const response = await axios.get(`${API_BASE_URL}/Equipment/list`);
      return response.data;
    },
    get: async (id) => {
      const response = await axios.get(`${API_BASE_URL}/Equipment/${id}`);
      return response.data;
    },
    create: async (equipmentData) => {
      const response = await axios.post(`${API_BASE_URL}/Equipment`, equipmentData);
      return response.data;
    },
    update: async (id, equipmentData) => {
      const response = await axios.put(`${API_BASE_URL}/Equipment/${id}`, equipmentData);
      return response.data;
    },
    delete: async (id) => {
      const response = await axios.delete(`${API_BASE_URL}/Equipment/${id}`);
      return response.data;
    }
  },

  // Operations API
  operations: {
    getAll: async () => {
      const response = await axios.get(`${API_BASE_URL}/Operation/list`);
      return response.data;
    },
    get: async (id) => {
      const response = await axios.get(`${API_BASE_URL}/Operation/${id}`);
      return response.data;
    },
    create: async (operationData) => {
      const response = await axios.post(`${API_BASE_URL}/Operation`, operationData);
      return response.data;
    },
    update: async (id, operationData) => {
      const response = await axios.put(`${API_BASE_URL}/Operation/${id}`, operationData);
      return response.data;
    },
    delete: async (id) => {
      const response = await axios.delete(`${API_BASE_URL}/Operation/${id}`);
      return response.data;
    }
  }
};

export default api;
